import { OrderUnitOfMeasure } from '@treadinc/horizon-api-spec';
import { startCase } from 'lodash-es';
import { useMemo } from 'react';
import { FieldValues } from 'react-hook-form';

import { getUomDisplayOverride } from '~constants/enums';
import { useAllowedOrderUnitOfMeasures } from '~hooks/useAllowedUnitOfMeasureOptions';

import AutocompleteField from '../v2/Autocomplete/AutocompleteField';
import { AutocompleteFieldProps } from '../v2/Autocomplete/types';

const unitKeys = Object.keys(OrderUnitOfMeasure) as (keyof typeof OrderUnitOfMeasure)[];
const unitOfMeasureOptions = unitKeys.map((unit) => {
  const value = OrderUnitOfMeasure[unit];

  return {
    label: getUomDisplayOverride(unit) ?? startCase(unit.toLowerCase()),
    value,
    _entity: value,
  };
});

type Props<T extends FieldValues> = Omit<
  AutocompleteFieldProps<T, OrderUnitOfMeasure>,
  'options'
> & { filterOptions?: OrderUnitOfMeasure[] };

export default function AutocompleteOrderUnitOfMeasure<T extends FieldValues>({
  filterOptions,
  ...rest
}: Props<T>) {
  const allowedUnits = useAllowedOrderUnitOfMeasures();

  const effectiveOptions = useMemo(() => {
    return unitOfMeasureOptions.filter(({ value }) => {
      const isAllowed = allowedUnits.length === 0 || allowedUnits.includes(value);
      const isFiltered = (filterOptions ?? []).includes(value);

      return isAllowed && !isFiltered;
    });
  }, [allowedUnits, filterOptions]);

  return <AutocompleteField {...rest} options={effectiveOptions} />;
}
